import React from "react";
import ReactDOM from "react-dom";
import {
  AuthClientEvent,
  AuthClientInitOptions,
  AuthClientTokens,
} from "@react-keycloak/core/lib/types";
import { APP_DEV, APP_NAME } from "./constants/environment";
import { AppConfig } from "./domain/appConfig";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Loader } from "./components/utils/Loader";
import { logInAction } from "./store/actions/authAction";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import globalStore from "./store/globalStore";
import localStore from "./store/localStore";
import keycloak from "./config/keycloak";
import Login from "./components/utils/Login";
import Logout from "./components/utils/Logout";
import PrivateRoute from "./components/utils/PrivateRoute";
import singleSpaReact from "single-spa-react";
import {
  deleteFromStorage,
  getFPKT,
  getFromStorage,
  removeFPKT,
  setFPKT,
  setToStorage,
} from "./services/handleLocalStorage";
import { keycloakInitOptions } from "./services/keycloakConfiguration";
import Parcel from "single-spa-react/parcel";
import { mountRootParcel, navigateToUrl } from "single-spa";
import { ERROR_EVENT, PREVIOUS_URL } from "./constants/storage";
import { AuthConfig } from "./domain/authConfig";
import { Provider } from "react-redux";

const App = ({ authConfig }: AppConfig) => {
  const appToken: AuthClientTokens | undefined = getFPKT(authConfig);
  const initOptions: AuthClientInitOptions | undefined =
    keycloakInitOptions(appToken);
  if (
    !window.location.href.endsWith("login") &&
    !window.location.href.endsWith("logout")
  ) {
    setToStorage(PREVIOUS_URL, window.location.pathname);
  }

  const eventKeycloak = (event: AuthClientEvent, error: unknown) => {
    switch (event) {
      case "onAuthError":
      case "onAuthRefreshError":
        setToStorage(ERROR_EVENT, event);
        console.error(event, error);
        window.location.reload();
        break;
      case "onInitError":
        setToStorage(ERROR_EVENT, event);
        console.error(event, error);
        navigateToUrl("/logout");
        break;
    }
    setToStorage("event", event);
  };
  const onTokens = (authClientTokens: AuthClientTokens) => {
    const event = getFromStorage("event");
    switch (event) {
      case "onReady":
        deleteFromStorage(ERROR_EVENT);
        break;
      case "onAuthLogout":
        removeFPKT();
        window.location.reload();
        break;
      case "onAuthSuccess":
      case "onTokenExpired":
      case "onAuthRefreshSuccess":
        setFPKT(authConfig, authClientTokens);
        setFPKT(
          { appName: "layout", client: "portal" } as AuthConfig,
          authClientTokens
        ); // always set to layout
        globalStore.DispatchAction(APP_NAME, logInAction(authClientTokens));
        break;
    }
  };

  const renderRoutes = (baseElement: JSX.Element) => {
    return (
      <Provider store={localStore}>
        <BrowserRouter>
          <Routes>
            <Route path={"*"} element={baseElement} />
            <Route path={"/login"} element={<Login />} />
            <Route
              path={"/logout"}
              element={
                appToken?.idToken && appToken.refreshToken ? (
                  <Logout
                    refreshToken={appToken.refreshToken}
                    idToken={appToken.idToken}
                  />
                ) : (
                  <Login />
                )
              }
            />
          </Routes>
        </BrowserRouter>
      </Provider>
    );
  };

  return APP_DEV ? (
    renderRoutes(<PrivateRoute />)
  ) : (
    <ReactKeycloakProvider
      initOptions={initOptions}
      authClient={keycloak(authConfig)}
      onTokens={onTokens}
      onEvent={eventKeycloak}
      LoadingComponent={
        getFromStorage(ERROR_EVENT) === undefined ? (
          <Parcel
            config={() => import("layout/ErrorServer")}
            mountParcel={mountRootParcel}
          />
        ) : (
          <Loader show={authConfig.loader} />
        )
      }
    >
      {renderRoutes(<PrivateRoute />)}
    </ReactKeycloakProvider>
  );
};

const lifecycles = singleSpaReact({
  React,
  ReactDOM,
  rootComponent: App,
  errorBoundary(err, errInfo, props) {
    console.error("Error occurred in single-spa-react application:", err);
    console.error("Error info:", errInfo);
    console.error("Props at the time of error:", props);
    return <div>error</div>;
  },
});

export const { bootstrap, mount, unmount } = lifecycles;

export default App;
